import { useState, useMemo } from "react";
import { Skeleton, Grid, Card, CardMedia, IconButton, Typography, Box, CardActionArea, CircularProgress } from "@mui/material";
import { Add, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ArticleControls from '../ArticleComponents/ArticleControls.js'
import ArticleCard from "../ArticleComponents/ArticleCard.js";
import DeleteArticleDialog from '../ArticleComponents/DeleteArticleDialog.js'
import { default as EventBus } from '../../utils/EventBus.js'
import Tags from "../Tags.js";
import CommunityEventNames from '../EventNames.js'
import { carouselPaginationEnabled } from '../utils.js'

export default function FeaturedArticles(props = {}) {
  const navigate = useNavigate()

  const {
    editable,
    news,
    featured,
    ouCorner,
    highlighted,
    ouCornerCategory,
    published,
    articles,
    loading,
    loadingMore,
    totalArticlesAvailable,
    moreAvailable,
    loadMoreArticles
  } = props

  const [startIndex, setStartIndex] = useState(0)
  const [hovering, setHovering] = useState()
  const [articleToDelete, setArticleToDelete] = useState()
  const [deleteArticleDialogOpen, setDeleteArticleDialogOpen] = useState(false)

  const viewArticle = (article) => {
    if (article.newsWebUrl) {
      window.open(article.newsWebUrl, "_blank", "noopener,noreferrer");
    } else {
      navigate(article.guid)
    }
  }

  const prevArticle = () => {
    setStartIndex(startIndex - 1)
  };

  const WINDOW = 3;

  const nextArticle = async () => {
    const nextIndex = startIndex + 1;

    if (nextIndex >= totalArticlesAvailable) {
      setStartIndex(0);
      return;
    }

    const needMore =
      nextIndex + (WINDOW - 1) >= articles.length &&
      (articles.length < totalArticlesAvailable || moreAvailable);

    if (needMore) {
      await loadMoreArticles()
    }

    setStartIndex(startIndex + 1);
  };

  const visibleArticles = useMemo(() => {
    if (!articles || articles.length === 0) return [];
    if (articles.length <= WINDOW) return articles

    const visible = [];
    for (let i = 0; i < WINDOW; i++) {
      const index = ((startIndex + i) % articles.length + articles.length) % articles.length;
      visible.push(articles[index]);
    }

    return visible;
  }, [articles, startIndex]);

  const addArticle = () => {
    EventBus.emit(CommunityEventNames.OPEN_ADD_EDIT_ARITCLE_DIALOG, {
      mode: 'add',
      article: {
        featured,
        news,
        ouCorner,
        highlighted,
        ouCornerCategory
      },
      visibility: {
        featured,
        news,
        ouCorner,
        highlighted,
        ouCornerCategory
      }
    })
  }

  const editArticle = (article) => {
    EventBus.emit(CommunityEventNames.OPEN_ADD_EDIT_ARITCLE_DIALOG, {
      article,
      visibility: {
        featured,
        news,
        ouCorner,
        highlighted,
        ouCornerCategory
      },
      articleId: article.guid,
      webUrl: article.webUrl,
      mode: 'edit',
      isDraft: article.isDraft
    })
  }

  const [showPrev, showNext] = carouselPaginationEnabled(WINDOW, articles?.length, totalArticlesAvailable, moreAvailable, startIndex)

  return (<>
    <DeleteArticleDialog
      open={deleteArticleDialogOpen}
      article={articleToDelete}
      onClose={() => {
        setArticleToDelete()
        setDeleteArticleDialogOpen(false)
      }}
    />

    {loading || visibleArticles?.length > 0 ? <Grid
      container
      gap={0}
      className={editable && !loading ? "article-section" : ''}
    >

      {editable && !loading && published && <Grid item xs={12} className="add-icon">
        <div className="article-controls-backdrop">
          <IconButton onClick={addArticle} size='medium' className="icon-button">
            <Add />
          </IconButton>
        </div>
      </Grid>}

      <Grid item sm={12} md={8} className='article-tile' sx={{ height: '350px' }}>
        {loading && <Skeleton key='loading-skele' className='fit-height' sx={{ position: 'relative' }}></Skeleton>}

        {!loading && visibleArticles[0] && 
        <Card sx={{ display: 'flex' }} className="featured-article" >
          <CardActionArea
            component='div'
            sx={{ display: 'flex' }}
            onMouseOver={() => setHovering(0)}
            onMouseOut={() => setHovering()}
          >
            <div className="article-click-container" onClick={() => viewArticle(visibleArticles[0])}>
              <CardMedia component="div" sx={visibleArticles[0]?.image ? { backgroundImage: `url(${visibleArticles[0]?.image})` } : undefined} alt="Featured Article" className="featured-image" />
              <CardMedia component="div" sx={visibleArticles[0]?.image ? { backgroundImage: `url(${visibleArticles[0]?.image})` } : undefined} alt="Featured Article" className="featured-image-blurred" />

              <div style={{ zIndex: 3 }}>
                <Box className="gradient-overlay" />

                <Box className="article-info">
                  <div>
                    <Typography variant="h6" className="article-title">
                      {visibleArticles[0]?.title}
                    </Typography>
                    <Typography variant="body2" className="article-subtitle">
                      {visibleArticles[0]?.author} • {visibleArticles[0]?.date}
                    </Typography>
                  </div>

                  <Tags
                    tags={visibleArticles[0]?.tags || ""}
                    onSelectTag={(tag) => setHovering()}
                    clickable={published}
                    sx={{ marginTop: '4px' }}
                  />
                </Box>
              </div>

            </div>
            {loadingMore && <Box className="nav-buttons-loading">
              <CircularProgress color='inherit' size={30} thickness={4}/>
            </Box>}

            {!loadingMore && (showPrev || showNext) && <Box className={`nav-buttons${!showPrev && showNext ? ' no-previous' : ''}`}>
              {showPrev && !loadingMore && <IconButton size='small' className="nav-button left" onClick={prevArticle}>
                <ChevronLeft sx={{ color: 'white' }} fontSize="large" />
              </IconButton>}
              &nbsp;&nbsp;
              {showNext && !loadingMore && <IconButton size='small' className="nav-button right" onClick={nextArticle}>
                <ChevronRight sx={{ color: 'white' }} fontSize="large" />
              </IconButton>}
            </Box>}
            
            {editable && !loading && <ArticleControls
              isDraft={visibleArticles[0]?.isDraft}
              isPinned={visibleArticles[0]?.metadata?.isPinned}
              showAdd={false}
              hovering={hovering === 0}
              onClickEdit={() => editArticle(visibleArticles[0])}
              onClickDelete={() => {
                setArticleToDelete(visibleArticles[0])
                setDeleteArticleDialogOpen(true)
              }}
              article={visibleArticles[0]}
            />}
          </CardActionArea>

        </Card>}
      </Grid>

      <Grid item sm={12} md={4} className='article-tile'>
        {loading && <Skeleton className='fit-height'></Skeleton>}
        {!loading && <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            flexGrow: 1,
          }}
          className="medium-articles-container">

          {visibleArticles.map((article, index) => {
            if (index === 0) return undefined;
            return <ArticleCard
              key={index}
              editable={editable && !loading}
              index={index}
              minHeight='165px'
              article={article}
              published={published}
              visibility={{
                news,
                featured,
                ouCorner,
                highlighted,
                ouCornerCategory,
              }}
            />
          })}

        </Box>}
      </Grid>
      
    </Grid> : undefined}
  </>);
}
